import type { MatchState } from './state';
import type { SimConfig } from '../config/simConfig';
import { PITCH } from '../config/pitchConfig';

/**
 * D40 cage walls (teamSize ≤ rules.WALLED_MAX_SIZE): small-sided FAFI is Bolzplatz football — a netted
 * cage with NO out of bounds. The ball rebounds off all four boards (restitution bounds.ballRestitution),
 * EXCEPT through the goal mouth (|y| < goalWidth/2 and below the bar), which stays open so goals work
 * unchanged. Players are fenced at the boards. Skipped during GOAL_CELEBRATION (the ball may rest in the
 * net). Infinite height — lofted balls rebound too (the net). Pure/deterministic.
 */
export function reflectCageWalls(state: MatchState, cfg: SimConfig): void {
  if (state.mode.teamSize > cfg.rules.WALLED_MAX_SIZE) return;
  if (state.phase === 'GOAL_CELEBRATION') return;
  const pitch = PITCH[state.mode.teamSize];
  const rest = cfg.bounds.ballRestitution;
  const b = state.ball;
  const r = cfg.ball.RADIUS;
  const wx = pitch.length / 2 - r;
  const wy = pitch.width / 2 - r;
  const inMouth = Math.abs(b.pos.y) < pitch.goalWidth / 2 && b.pos.z < pitch.barH;
  if (!inMouth) {
    if (b.pos.x < -wx) {
      b.pos.x = -wx;
      if (b.vel.x < 0) b.vel.x = -b.vel.x * rest;
    } else if (b.pos.x > wx) {
      b.pos.x = wx;
      if (b.vel.x > 0) b.vel.x = -b.vel.x * rest;
    }
  }
  if (b.pos.y < -wy) {
    b.pos.y = -wy;
    if (b.vel.y < 0) b.vel.y = -b.vel.y * rest;
  } else if (b.pos.y > wy) {
    b.pos.y = wy;
    if (b.vel.y > 0) b.vel.y = -b.vel.y * rest;
  }
  // players lean on the boards — fenced at the lines (goal mouth included: no hiding in the net)
  for (const p of state.players) {
    if (p.sentOff) continue; // D55: a red-carded player stands OFF the pitch — the cage must not shove him back in
    fenceCagePlayer(p, state.mode.teamSize, cfg);
  }
}

/** D40: fence ONE player at the cage boards. Shared with stepEntity so prediction keeps exact parity. */
export function fenceCagePlayer(p: { pos: { x: number; y: number }; vel: { x: number; y: number } }, teamSize: MatchState['mode']['teamSize'], cfg: SimConfig): void {
  if (teamSize > cfg.rules.WALLED_MAX_SIZE) return;
  const pitch = PITCH[teamSize];
  const px = pitch.length / 2 - 0.1;
  const py = pitch.width / 2 - 0.1;
  if (p.pos.x < -px) {
    p.pos.x = -px;
    if (p.vel.x < 0) p.vel.x = 0;
  } else if (p.pos.x > px) {
    p.pos.x = px;
    if (p.vel.x > 0) p.vel.x = 0;
  }
  if (p.pos.y < -py) {
    p.pos.y = -py;
    if (p.vel.y < 0) p.vel.y = 0;
  } else if (p.pos.y > py) {
    p.pos.y = py;
    if (p.vel.y > 0) p.vel.y = 0;
  }
}

/**
 * Arena player containment (dev safety net). Keeps players inside a wall `margin` metres outside the
 * pitch (outward velocity zeroed). Config-gated (`bounds.enabled`, default off) and pure/deterministic.
 * The BALL is governed by the rules slice (§6 goals + out-of-play → restarts) — never wall-reflected —
 * so this only fences players, which have no out-of-play concept. Mutates `state`.
 */
export function containBounds(state: MatchState, cfg: SimConfig): void {
  if (!cfg.bounds.enabled) return;
  const pitch = PITCH[state.mode.teamSize];
  const bx = pitch.length / 2 + cfg.bounds.margin;
  const by = pitch.width / 2 + cfg.bounds.margin;

  for (const p of state.players) {
    if (p.pos.x < -bx) {
      p.pos.x = -bx;
      if (p.vel.x < 0) p.vel.x = 0;
    } else if (p.pos.x > bx) {
      p.pos.x = bx;
      if (p.vel.x > 0) p.vel.x = 0;
    }
    if (p.pos.y < -by) {
      p.pos.y = -by;
      if (p.vel.y < 0) p.vel.y = 0;
    } else if (p.pos.y > by) {
      p.pos.y = by;
      if (p.vel.y > 0) p.vel.y = 0;
    }
  }
}
